import { c as _c } from "react-compiler-runtime";
import { CalendarDate, DateFormatter, GregorianCalendar, createCalendar } from "@internationalized/date";
import { useFormValidationState } from "@react-stately/form";
import { useControlledState } from "@react-stately/utils";
import React from "react";
import { useLocale } from "react-aria";
import { useDateFieldState } from "react-stately";
import useI18n from "./useI18n.js";
const opts = {
  year: "numeric",
  month: "2-digit"
};
const defaultMinValue = {
  year: 1900,
  month: 1
};
export function useMonthYearDateFieldState(t0) {
  const $ = _c(57);
  const {
    isDisabled: t1,
    isReadOnly: t2,
    isRequired: t3,
    minValue: t4,
    maxValue,
    value: propValue,
    defaultValue,
    onChange
  } = t0;
  const isDisabled = t1 === undefined ? false : t1;
  const isReadOnly = t2 === undefined ? false : t2;
  const isRequired = t3 === undefined ? false : t3;
  const minValue = t4 === undefined ? defaultMinValue : t4;
  const {
    locale
  } = useLocale();
  let t5;
  if ($[0] !== onChange) {
    t5 = v => {
      onChange?.({
        year: v?.year != null && v.year > 0 ? v.year : null,
        month: v?.month != null && v.month > 0 ? v.month : null
      });
    };
    $[0] = onChange;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const [value, setDate] = useControlledState(propValue, defaultValue ?? null, t5);
  const [year, setYear] = React.useState(propValue?.year ?? 0);
  const [month, setMonth] = React.useState(propValue?.month ?? 0);
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = new GregorianCalendar();
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const calendar = t6;
  let t7;
  if ($[3] !== locale) {
    t7 = new DateFormatter(locale, opts);
    $[3] = locale;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const dateFormatter = t7;
  const isPlaceholderYear = year === 0;
  const isPlaceholderMonth = month === 0;
  const {
    placeholderYear,
    placeholderMonth,
    literal
  } = usePlaceholderValues();
  const t8 = isPlaceholderYear ? placeholderYear : String(year);
  let t9;
  if ($[5] !== isPlaceholderYear || $[6] !== placeholderYear || $[7] !== t8) {
    t9 = {
      type: "year",
      text: t8,
      isPlaceholder: isPlaceholderYear,
      placeholder: placeholderYear,
      isEditable: true,
      minValue: 1,
      maxValue: 9999
    };
    $[5] = isPlaceholderYear;
    $[6] = placeholderYear;
    $[7] = t8;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== literal) {
    t10 = {
      type: "literal",
      text: literal,
      isPlaceholder: false,
      placeholder: literal,
      isEditable: false
    };
    $[9] = literal;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== isPlaceholderMonth || $[12] !== month || $[13] !== placeholderMonth) {
    t11 = isPlaceholderMonth ? placeholderMonth : String(month).padStart(2, "0");
    $[11] = isPlaceholderMonth;
    $[12] = month;
    $[13] = placeholderMonth;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== isPlaceholderMonth || $[16] !== placeholderMonth || $[17] !== t11) {
    t12 = {
      type: "month",
      text: t11,
      isPlaceholder: isPlaceholderMonth,
      placeholder: placeholderMonth,
      isEditable: true,
      minValue: 1,
      maxValue: 12
    };
    $[15] = isPlaceholderMonth;
    $[16] = placeholderMonth;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t10 || $[20] !== t12 || $[21] !== t9) {
    t13 = [t9, t10, t12];
    $[19] = t10;
    $[20] = t12;
    $[21] = t9;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const segments = t13;
  const builtinValidation = useValidationResult(value, maxValue, minValue);
  let t14;
  if ($[23] !== value) {
    t14 = monthYearToFormValue(value);
    $[23] = value;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== builtinValidation || $[26] !== t14) {
    t15 = {
      isInvalid: false,
      value: t14,
      builtinValidation
    };
    $[25] = builtinValidation;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  const validation = useFormValidationState(t15);
  const isValueInvalid = validation.displayValidation.isInvalid;
  const validationState = isValueInvalid ? "invalid" : null;
  let t16;
  if ($[28] !== month || $[29] !== setDate || $[30] !== year) {
    t16 = (part, delta) => {
      if (part === "year") {
        setYear(prev => {
          const newYear = prev + delta;
          setDate({
            year: newYear,
            month
          });
          return newYear;
        });
      } else {
        if (part === "month") {
          setMonth(prev_0 => {
            let newMonth = prev_0 + delta;
            if (newMonth < 1) {
              setYear(_temp);
              newMonth = 12;
            } else {
              if (newMonth > 12) {
                setYear(_temp2);
                newMonth = 1;
              }
            }
            setDate({
              year,
              month: newMonth
            });
            return newMonth;
          });
        }
      }
    };
    $[28] = month;
    $[29] = setDate;
    $[30] = year;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  const adjustSegment = t16;
  let t17;
  if ($[32] !== value) {
    t17 = monthYearToDateValue(value);
    $[32] = value;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== value) {
    t18 = monthYearToJsDate(value);
    $[34] = value;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== value) {
    t19 = monthYearToFormValue(value) ?? "";
    $[36] = value;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  const t20 = month > 0 ? month : null;
  const t21 = year > 0 ? year : null;
  let t22;
  if ($[38] !== adjustSegment || $[39] !== dateFormatter || $[40] !== isDisabled || $[41] !== isReadOnly || $[42] !== isRequired || $[43] !== isValueInvalid || $[44] !== month || $[45] !== segments || $[46] !== setDate || $[47] !== t17 || $[48] !== t18 || $[49] !== t19 || $[50] !== t20 || $[51] !== t21 || $[52] !== validation || $[53] !== validationState || $[54] !== value || $[55] !== year) {
    t22 = {
      ...validation,
      isInvalid: isValueInvalid,
      value: t17,
      defaultValue: null,
      dateValue: t18,
      monthYearTextValue: t19,
      month: t20,
      year: t21,
      calendar,
      setValue(value_0) {
        if (value_0 == null) {
          setYear(0);
          setMonth(0);
          setDate(null);
        } else {
          const newYear_0 = value_0.year;
          const newMonth_0 = value_0.month;
          setYear(newYear_0);
          setMonth(newMonth_0);
          setDate({
            year: newYear_0,
            month: newMonth_0
          });
        }
        validation.commitValidation();
      },
      segments,
      dateFormatter,
      granularity: "day",
      maxGranularity: "year",
      isDisabled,
      isReadOnly,
      isRequired,
      increment(part_0) {
        adjustSegment(part_0, 1);
      },
      decrement(part_1) {
        adjustSegment(part_1, -1);
      },
      incrementPage(_part) {
        throw new Error("Function not implemented.");
      },
      decrementPage(_part_0) {
        throw new Error("Function not implemented.");
      },
      setSegment(part_2, v_0) {
        if (part_2 === "year") {
          const newYear_1 = Number.parseInt(v_0, 10);
          setYear(newYear_1);
          setDate({
            year: newYear_1,
            month
          });
        } else {
          if (part_2 === "month") {
            const newMonth_1 = Number.parseInt(v_0, 10);
            if (newMonth_1 >= 1 && newMonth_1 <= 12) {
              setMonth(newMonth_1);
              setDate({
                year,
                month: newMonth_1
              });
            }
          }
        }
      },
      confirmPlaceholder() {
        if (isDisabled || isReadOnly) {
          return;
        }
      },
      clearSegment(type) {
        if (type === "year") {
          setYear(0);
          setDate(null);
        } else {
          if (type === "month") {
            setMonth(0);
            setDate({
              year,
              month: null
            });
          }
        }
      },
      formatValue(_fieldOptions) {
        if (!value) {
          return "";
        }
        return dateFormatter.format(monthYearToJsDate(value));
      },
      getDateFormatter(locale_0) {
        return new DateFormatter(locale_0, opts);
      },
      validationState
    };
    $[38] = adjustSegment;
    $[39] = dateFormatter;
    $[40] = isDisabled;
    $[41] = isReadOnly;
    $[42] = isRequired;
    $[43] = isValueInvalid;
    $[44] = month;
    $[45] = segments;
    $[46] = setDate;
    $[47] = t17;
    $[48] = t18;
    $[49] = t19;
    $[50] = t20;
    $[51] = t21;
    $[52] = validation;
    $[53] = validationState;
    $[54] = value;
    $[55] = year;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  return t22;
}
function _temp2(prevYear_0) {
  return prevYear_0 + 1;
}
function _temp(prevYear) {
  return prevYear - 1;
}
function compareMonthYear(a, b) {
  if (a.year === null && b.year === null) {
    if (a.month === null && b.month === null) return 0;
    if (a.month === null) return -1;
    if (b.month === null) return 1;
    return a.month - b.month;
  }
  if (a.year === null) return -1;
  if (b.year === null) return 1;
  if (a.year !== b.year) {
    return a.year - b.year;
  }
  if (a.month === null && b.month === null) return 0;
  if (a.month === null) return -1;
  if (b.month === null) return 1;
  return a.month - b.month;
}
function useValidationResult(value, minValue, maxValue) {
  const $ = _c(21);
  const formatter = useI18n();
  const rangeOverflow = value != null && maxValue != null && compareMonthYear(value, maxValue) > 0;
  const rangeUnderflow = value != null && minValue != null && compareMonthYear(value, minValue) < 0;
  const isInvalid = rangeOverflow || rangeUnderflow;
  let errors;
  if ($[0] !== formatter || $[1] !== isInvalid || $[2] !== maxValue || $[3] !== minValue || $[4] !== rangeOverflow || $[5] !== rangeUnderflow) {
    errors = [];
    if (isInvalid) {
      if (rangeUnderflow) {
        let t0;
        if ($[7] !== formatter || $[8] !== minValue) {
          t0 = formatter.format("datePickerUnderflow", {
            minValue: monthYearToFormValue(minValue) ?? ""
          });
          $[7] = formatter;
          $[8] = minValue;
          $[9] = t0;
        } else {
          t0 = $[9];
        }
        errors.push(t0);
      }
      if (rangeOverflow) {
        let t0;
        if ($[10] !== formatter || $[11] !== maxValue) {
          t0 = formatter.format("datePickerOverflow", {
            maxValue: monthYearToFormValue(maxValue) ?? ""
          });
          $[10] = formatter;
          $[11] = maxValue;
          $[12] = t0;
        } else {
          t0 = $[12];
        }
        errors.push(t0);
      }
    }
    $[0] = formatter;
    $[1] = isInvalid;
    $[2] = maxValue;
    $[3] = minValue;
    $[4] = rangeOverflow;
    $[5] = rangeUnderflow;
    $[6] = errors;
  } else {
    errors = $[6];
  }
  const t0 = !isInvalid;
  let t1;
  if ($[13] !== rangeOverflow || $[14] !== rangeUnderflow || $[15] !== t0) {
    t1 = {
      badInput: false,
      customError: false,
      patternMismatch: false,
      rangeOverflow,
      rangeUnderflow,
      stepMismatch: false,
      tooLong: false,
      tooShort: false,
      typeMismatch: false,
      valueMissing: false,
      valid: t0
    };
    $[13] = rangeOverflow;
    $[14] = rangeUnderflow;
    $[15] = t0;
    $[16] = t1;
  } else {
    t1 = $[16];
  }
  let t2;
  if ($[17] !== errors || $[18] !== isInvalid || $[19] !== t1) {
    t2 = {
      isInvalid,
      validationErrors: errors,
      validationDetails: t1
    };
    $[17] = errors;
    $[18] = isInvalid;
    $[19] = t1;
    $[20] = t2;
  } else {
    t2 = $[20];
  }
  return t2;
}
function monthYearToDateValue(value) {
  if (value?.year == null) {
    return null;
  }
  return new CalendarDate(value.year, value.month ?? 1, 1);
}
function monthYearToFormValue(value) {
  if (value?.year == null) {
    return null;
  }
  if (value.month == null) {
    return String(value.year);
  }
  return `${value.year}-${String(value.month).padStart(2, "0")}`;
}
const placeholder = new Date();
function monthYearToJsDate(value) {
  if (value?.year == null) {
    return placeholder;
  }
  return new Date(value.year, value.month ?? 0, 1);
}
function usePlaceholderValues() {
  const $ = _c(12);
  const {
    locale
  } = useLocale();
  let t0;
  if ($[0] !== locale) {
    t0 = {
      locale,
      granularity: "day",
      createCalendar
    };
    $[0] = locale;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    segments
  } = useDateFieldState(t0);
  let yearMonthSeparator;
  if (segments.at(-1)?.type === "year") {
    let t1;
    if ($[2] !== segments) {
      t1 = segments.at(-2)?.text ?? "/";
      $[2] = segments;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    yearMonthSeparator = t1;
  } else {
    yearMonthSeparator = segments[1].text;
  }
  let t1;
  if ($[4] !== segments) {
    t1 = segments.find(_temp3);
    $[4] = segments;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const yearSegment = t1;
  let t2;
  if ($[6] !== segments) {
    t2 = segments.find(_temp4);
    $[6] = segments;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const monthSegment = t2;
  const t3 = yearSegment?.placeholder ?? "yyyy";
  const t4 = monthSegment?.placeholder ?? "mm";
  let t5;
  if ($[8] !== t3 || $[9] !== t4 || $[10] !== yearMonthSeparator) {
    t5 = {
      placeholderYear: t3,
      placeholderMonth: t4,
      literal: yearMonthSeparator
    };
    $[8] = t3;
    $[9] = t4;
    $[10] = yearMonthSeparator;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function _temp4(s_0) {
  return s_0.type === "month";
}
function _temp3(s) {
  return s.type === "year";
}
//# sourceMappingURL=useMonthYearDateFieldState.js.map